import { NavLink, Text } from '@mantine/core';
import {
  IconDashboard, IconMessageCircle, IconMail,
  IconRobot, IconAddressBook, IconShield, IconCoin, IconCreditCard, IconBuildingCommunity, IconHome,
  IconTimeline, IconListCheck, IconAutomation, IconBug, IconActivity, IconKey,
  IconExternalLink, IconChartBar,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { type ColourZone, ZONE_COLOURS } from '../theme';

export interface NavItem {
  label: string;
  icon: React.ElementType;
  path: string;
  zone: ColourZone;
  external?: boolean; // open in new tab instead of in-app navigation
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

// Single source of truth for navigation, page titles, and header zone colour.
// AppShell derives both from here — do not reintroduce separate route maps.
export const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Daily',
    items: [
      { label: 'Dashboard', icon: IconDashboard, path: '/', zone: 'teal' },
      { label: 'Work Progress', icon: IconListCheck, path: '/work-progress', zone: 'yellow' },
    ],
  },
  {
    label: 'Comms',
    items: [
      { label: 'Chat', icon: IconMessageCircle, path: '/chat', zone: 'yellow' },
      { label: 'Email', icon: IconMail, path: '/email', zone: 'teal' },
      { label: 'Email Timeline', icon: IconTimeline, path: '/email-timeline', zone: 'teal' },
      { label: 'Contacts', icon: IconAddressBook, path: '/contacts', zone: 'teal' },
    ],
  },
  {
    label: 'Money',
    items: [
      { label: 'Cash Flow', icon: IconChartBar, path: '/cashflow', zone: 'teal' },
      { label: 'Subscriptions', icon: IconCreditCard, path: '/subscriptions', zone: 'teal' },
      { label: 'Usage', icon: IconCoin, path: '/usage', zone: 'teal' },
    ],
  },
  {
    label: 'Property',
    items: [
      { label: 'Body Corp', icon: IconBuildingCommunity, path: '/bodycorp', zone: 'teal' },
      { label: 'Tenancy', icon: IconHome, path: '/tenancy', zone: 'teal' },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Agents', icon: IconRobot, path: '/agents', zone: 'yellow' },
      { label: 'Automations', icon: IconAutomation, path: '/automations', zone: 'teal' },
      { label: 'Monitoring', icon: IconActivity, path: '/monitoring', zone: 'green' },
      { label: 'Issues', icon: IconBug, path: '/issues', zone: 'orange' },
      { label: 'Legal Register', icon: IconShield, path: '/legal', zone: 'green' },
      // API Tracker — admin-only registry of all API keys + last-used data.
      // Served by claude-bot at backend.risky.net.au/api-tracker (separate origin),
      // so opens in a new tab; the page has its own bearer-token prompt.
      { label: 'API Keys', icon: IconKey, path: 'https://backend.risky.net.au/api-tracker', zone: 'green', external: true },
    ],
  },
];
// Removed 2026-05-15: Knowledge, RI Search, Timeline — pages were not working
// (Knowledge → /knowledge, RI Search → /knowledge-search, Timeline → /timeline).
// If they come back, restore the imports + nav entries and matching App.tsx routes.

/** The nav item owning a route, for header title + zone. */
export function findNavItem(pathname: string): NavItem | undefined {
  for (const section of NAV_SECTIONS) {
    const hit = section.items.find((i) => !i.external && i.path === pathname);
    if (hit) return hit;
  }
  return undefined;
}

function NavItemLink({ item }: { item: NavItem }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isActive = !item.external && pathname === item.path;
  const zoneColour = ZONE_COLOURS[item.zone];

  function handleClick() {
    if (item.external) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(item.path);
  }

  return (
    <NavLink
      label={item.label}
      leftSection={<item.icon size={20} />}
      rightSection={item.external ? <IconExternalLink size={14} style={{ opacity: 0.6 }} /> : undefined}
      active={isActive}
      color={zoneColour}
      onClick={handleClick}
      variant="light"
    />
  );
}

export function NavItems() {
  return (
    <>
      {NAV_SECTIONS.map((section) => (
        <div key={section.label}>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" px="sm" pt="sm" pb={4} style={{ letterSpacing: '0.05em' }}>
            {section.label}
          </Text>
          {section.items.map((item) => (
            <NavItemLink key={item.path} item={item} />
          ))}
        </div>
      ))}
    </>
  );
}
